import { useDebounceFn } from '@vueuse/core';
import { useLazyQuery } from '@vue/apollo-composable';

import type { Student, StudentFilter } from '@/types/composition-functions';
import { SEARCH_STUDENTS } from '@/graphql/Queries';

interface UseStudentSearchOptions {
  initialSearchTerm?: string;
  query?: import('graphql').DocumentNode;
  filter?: Partial<StudentFilter>;
  debounceMs?: number;
  minLength?: number;
}

export function useStudentSearch(options?: UseStudentSearchOptions) {
  const studentSearchTerm = ref(options?.initialSearchTerm || '');
  const selectedStudent = ref<Student | null>(null);
  const filteredStudents = ref<Student[]>([]);
  const minLength = options?.minLength ?? 2;

  const { result: studentsResult, load: loadStudents, refetch } = useLazyQuery(
    options?.query || SEARCH_STUDENTS,
    undefined,
    { fetchPolicy: 'cache-and-network' },
  );

  const runSearch = (term: string) => {
    const filter: StudentFilter = { ...options?.filter, search: term };
    const variables = { first: 10, page: 1, filter };
    if (!loadStudents(undefined, variables)) {
      refetch(variables);
    }
  };

  const debouncedSearch = useDebounceFn(runSearch, options?.debounceMs ?? 250);

  const searchStudents = (event: { query: string }) => {
    studentSearchTerm.value = event.query;
    if (event.query.length >= minLength) {
      debouncedSearch(event.query);
    } else {
      filteredStudents.value = [];
    }
  };

  watch(studentsResult, (newResult) => {
    if (newResult?.students?.data) {
      filteredStudents.value = newResult.students.data.map((student: Student) => ({
        ...student,
        fullName: `${student.first_name} ${student.surname} (${student.matric_no})`,
      }));
    }
  });

  const onStudentSelect = (event: { value: Student }) => {
    selectedStudent.value = event.value;
  };

  return {
    studentSearchTerm,
    selectedStudent,
    filteredStudents,
    searchStudents,
    onStudentSelect,
  };
}
